import React, { useMemo, useEffect, useCallback, useState } from "react";
import { useNavigate } from "react-router";
import {
  Button,
  CommandInput,
  CommandList,
  CommandGroup,
  CommandItem,
  toast,
} from "@exiftrator/shared/components/ui";
import { LockKeyhole } from "lucide-react";
import { usePresets } from "../../contexts/PresetsContext";
import { useFeatureAccess } from "../../contexts/LicenseContext";
import { useBasePath } from "../../contexts/BasePathContext";
import { EXIF_FIELDS } from "../../config/fieldDefinitions";
import useFocusRestore from "../../hooks/useFocusRestore";
import SplitCommandModalShell from "./SplitCommandModalShell";
import {
  buildPresetPreviewSections,
  filterNonGpsPresets,
  getApplyActionLabel,
  getSelectedTargetCount,
} from "./modalUtils";

function PresetCommandModal({
  open,
  onOpenChange,
  selectedIndices,
  selectedCellRange,
  onApplyPreset,
}) {
  const navigate = useNavigate();
  const { appPath } = useBasePath();
  const presetsAccess = useFeatureAccess("presets");
  const { getAllPresets } = usePresets();

  const allPresets = getAllPresets();
  const filteredPresets = useMemo(
    () => filterNonGpsPresets(allPresets),
    [allPresets],
  );

  const [searchQuery, setSearchQuery] = useState("");
  const [selectedPresetId, setSelectedPresetId] = useState(null);

  useFocusRestore(open);

  const selectedCount = useMemo(
    () => getSelectedTargetCount(selectedIndices, selectedCellRange),
    [selectedCellRange, selectedIndices],
  );

  const visiblePresets = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();

    if (!query) {
      return filteredPresets;
    }

    return filteredPresets.filter((preset) => {
      if (preset.name.toLowerCase().includes(query)) {
        return true;
      }

      const sections = buildPresetPreviewSections(preset);
      return sections.some((section) =>
        section.items.some(
          (item) =>
            item.label.toLowerCase().includes(query) ||
            item.value.toLowerCase().includes(query),
        ),
      );
    });
  }, [filteredPresets, searchQuery]);

  useEffect(() => {
    if (!open) {
      setSearchQuery("");
      setSelectedPresetId(null);
      return;
    }

    if (visiblePresets.length === 0) {
      setSelectedPresetId(null);
      return;
    }

    const hasSelected = visiblePresets.some(
      (preset) => String(preset.id) === String(selectedPresetId),
    );
    if (!hasSelected) {
      setSelectedPresetId(visiblePresets[0].id);
    }
  }, [open, selectedPresetId, visiblePresets]);

  const selectedPreset = useMemo(
    () =>
      visiblePresets.find(
        (preset) => String(preset.id) === String(selectedPresetId),
      ) || null,
    [selectedPresetId, visiblePresets],
  );

  const selectedSections = useMemo(
    () => buildPresetPreviewSections(selectedPreset),
    [selectedPreset],
  );
  const applyLabel = getApplyActionLabel(selectedCount, "Apply preset");

  const handleApplyPreset = useCallback(
    (preset) => {
      if (!preset) return;

      if (!presetsAccess.allowed) {
        return;
      }

      if (selectedCount === 0) {
        toast.info("Select files to apply preset");
        return;
      }

      onApplyPreset?.(preset);
      onOpenChange(false);
    },
    [onApplyPreset, onOpenChange, presetsAccess.allowed, selectedCount],
  );

  const handleCommandValueChange = useCallback(
    (value) => {
      if (!value || value === selectedPresetId) {
        return;
      }

      const preset = visiblePresets.find(
        (item) => String(item.id) === String(value),
      );
      if (preset) {
        setSelectedPresetId(preset.id);
      }
    },
    [selectedPresetId, visiblePresets],
  );

  useEffect(() => {
    if (!open || !presetsAccess.allowed) return;

    const onKeyDown = (event) => {
      if (event.key === "Enter" && selectedPreset) {
        event.preventDefault();
        handleApplyPreset(selectedPreset);
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, presetsAccess.allowed, selectedPreset, handleApplyPreset]);

  const getPresetSubtitle = (preset) => {
    const fieldLabels = (preset.includedFields || [])
      .map((fieldId) => EXIF_FIELDS[fieldId]?.label || fieldId)
      .filter(Boolean);
    const fieldCount = fieldLabels.length;
    const fieldCountLabel = `${fieldCount} field${fieldCount === 1 ? "" : "s"}`;

    return fieldLabels.length > 0
      ? `${fieldCountLabel} • ${fieldLabels.join(", ")}`
      : fieldCountLabel;
  };

  const results = !presetsAccess.allowed ? (
    <div className="flex h-full items-center justify-center p-6">
      <div className="max-w-[280px] space-y-3 text-center">
        <LockKeyhole className="mx-auto size-5 text-muted-foreground" />
        <p className="text-sm text-muted-foreground">
          Presets are a Pro feature.
        </p>
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            onOpenChange(false);
            navigate(appPath("/settings/license"));
          }}
        >
          Unlock Pro
        </Button>
      </div>
    </div>
  ) : (
    <CommandList className="min-h-0 max-h-none overflow-y-auto p-1">
      {visiblePresets.length === 0 ? (
        <div className="py-10 text-center text-sm text-muted-foreground">
          {filteredPresets.length === 0
            ? (
              <>
                No presets yet. Create one in{" "}
                <button
                  type="button"
                  className="font-medium text-foreground underline underline-offset-2 hover:text-foreground/80"
                  onClick={() => {
                    onOpenChange(false);
                    navigate(appPath("/settings/presets"));
                  }}
                >
                  Settings
                </button>
                .
              </>
            )
            : "No presets match this search."}
        </div>
      ) : (
        <CommandGroup heading="Presets">
          {visiblePresets.map((preset) => (
            <CommandItem
              key={preset.id}
              value={String(preset.id)}
              onSelect={() => setSelectedPresetId(preset.id)}
              className="cursor-pointer transition-colors hover:bg-accent/60 hover:text-accent-foreground"
            >
              <div className="flex min-w-0 flex-1 flex-col">
                <span className="truncate text-sm font-medium">
                  {preset.name}
                </span>
                <span className="truncate text-xs font-mono text-muted-foreground">
                  {getPresetSubtitle(preset)}
                </span>
              </div>
            </CommandItem>
          ))}
        </CommandGroup>
      )}
    </CommandList>
  );

  const preview = !presetsAccess.allowed ? (
    <div className="flex h-full items-center justify-center p-6 text-center">
      <div className="space-y-2">
        <p className="text-sm font-medium text-foreground">
          Presets are locked
        </p>
        <p className="text-xs text-muted-foreground">
          Upgrade to apply saved presets to selected files.
        </p>
      </div>
    </div>
  ) : selectedPreset ? (
    <div className="flex h-full min-h-0 flex-col">
      <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-4 py-3">
        {selectedSections.length === 0 ? (
          <div className="rounded-md border border-dashed border-border p-4 text-xs text-muted-foreground">
            No values set
          </div>
        ) : (
          selectedSections.map((section) => (
            <section key={section.id} className="space-y-2">
              <h4 className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                {section.label}
              </h4>
              <div className="space-y-1.5 rounded-md border border-border-subtle bg-muted/30 p-2.5">
                {section.items.map((item) => (
                  <div
                    key={item.fieldId}
                    className="grid grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)] gap-2"
                  >
                    <span className="truncate text-[11px] text-muted-foreground">
                      {item.label}
                    </span>
                    <span
                      className="truncate text-[11px] font-mono text-foreground"
                      title={item.value}
                    >
                      {item.value}
                    </span>
                  </div>
                ))}
              </div>
            </section>
          ))
        )}
      </div>
    </div>
  ) : (
    <div className="h-full" />
  );

  const footer = (
    <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
      <span className="text-xs text-muted-foreground">Esc to close</span>
      <div className="flex w-full flex-col gap-2 md:w-auto md:flex-row md:items-center md:justify-end">
        <Button
          variant="outline"
          size="sm"
          className="hidden md:inline-flex"
          onClick={() => onOpenChange(false)}
        >
          Cancel
        </Button>
        <Button
          size="sm"
          className="w-full md:w-auto"
          onClick={() => handleApplyPreset(selectedPreset)}
          disabled={
            !presetsAccess.allowed || selectedCount === 0 || !selectedPreset
          }
        >
          {applyLabel}
        </Button>
      </div>
    </div>
  );

  return (
    <SplitCommandModalShell
      open={open}
      onOpenChange={onOpenChange}
      title="Preset picker"
      description="Search and apply a metadata preset"
      search={
        <CommandInput
          value={searchQuery}
          onValueChange={setSearchQuery}
          placeholder="Search presets..."
          disabled={!presetsAccess.allowed}
        />
      }
      results={results}
      preview={preview}
      footer={footer}
      mobileResultsLabel="Results"
      mobilePreviewLabel="Preset"
      commandProps={{
        shouldFilter: false,
        className: "bg-transparent",
        value: selectedPresetId ? String(selectedPresetId) : "",
        onValueChange: handleCommandValueChange,
      }}
    />
  );
}

export default PresetCommandModal;
